import React, { useCallback, useEffect, useState } from 'react';
import { List, notification, Spin } from 'antd';
import { IOnSubmitArgs, OpForm } from 'components/customAntd/DLS/OpForm/OpForm';
import { Role } from 'types/roleTypes';
import { AppDispatch, RootState } from 'store/store';
import { useDispatch, useSelector } from 'react-redux';
import { deleteRequest, getRequest, patchRequest } from 'api/apiClient';
import { OpTabs } from 'components/customAntd/DLS/OpTabs/OpTabs';
import { OpButton } from 'components/customAntd/DLS/OpButton/OpButton';
import { Integration } from 'types/integrationTypes';
import { CustomFormButtons } from 'components/pages/configuration/invitations/CustomFormButtons';
import axios from 'axios';
import { useConfirmModal } from 'utils/customHooks/useConfirmModal';
import { LoadingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { fetchIntegrationStatus } from 'store/slices/integrationSlice';

interface IntegrationConfigureProps {
    setIsConfigureOpen: (open: boolean) => void;
    setIsInstalled: (open: boolean) => void;
}

const IntegrationConfigure: React.FC<IntegrationConfigureProps> = ({ setIsConfigureOpen, setIsInstalled }) => {
    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const dispatch: AppDispatch = useDispatch();

    const [activeKey, setActiveKey] = useState<string>('organization');
    const [integrationData, setIntegrationData] = useState<Integration>();
    const [email, setEmail] = useState<string>('');
    const [savedValues, setSavedValues] = useState<any>();
    const [orgOptions, setOrgOptions] = useState<{ value: string; label: string, userId: number }[]>([]);
    const [entryIdOptions, setEntryIdOptions] = useState<{ value: number; label: string }[]>([]);
    // eslint-disable-next-line
    const [roles, setRoles] = useState<Role[]>([]);

    // eslint-disable-next-line
    const [isImportUsersEnabled, setIsImportUsersEnabled] = useState<boolean>(false);
    const [isGuestPassGenerationEnabled, setIsGuestPassGenerationEnabled] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    const confirmModal = useConfirmModal();

    const [form] = OpForm.useForm();

    const { t } = useTranslation();
    useEffect(() => {
        setActiveKey('organization');
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const integrationData = await getRequest(`/orgs/${orgId}/integrationMsiAlta`);
                const initialData = integrationData.data[0];

                const msiAltaApiHost = integrationData.data[0].msiAltaApiHost || 'https://api.openpath.com';

                const describeTokenResponse = await axios.get(`${msiAltaApiHost}/auth/accessTokens/${integrationData.data[0].msiAltaToken}`);
                const orgList = describeTokenResponse.data.data.tokenScopeList
                    .filter((scope: any) => scope.org.id !== null)
                    .map((scope: any) => ({
                        value: scope.org.id,
                        label: scope.org.name,
                        userId: scope.user.id
                    }));
                setOrgOptions(orgList);

                const describeUserResponse = await axios.get(`${msiAltaApiHost}/orgs/${initialData.msiAltaOrgId}/users/${initialData.msiAltaUserId}`, {
                    headers: {
                        Authorization: `${initialData.msiAltaToken}`,
                    },
                });
                setEmail(describeUserResponse.data.data.identity.email);

                const listAllEntriesResponse = await getRequest(`/orgs/${orgId}/integrationMsiAltaEntry`);
                const defaultEntryList = listAllEntriesResponse.data
                    .map((defaultEntry: any) => ({
                        value: defaultEntry.id,
                        label: defaultEntry.name,
                    }));
                setEntryIdOptions(defaultEntryList);

                const transformedInitialValues = {
                    ...initialData,
                    msiAltaEntrieIds: initialData.entryIds ? initialData.entryIds.map((id: string) => Number(id)) : undefined,
                    importUsers: initialData.importUsers === 1 ? true : false,
                    allowMsiAltaLogin: initialData.allowMsiAltaLogin === 1 ? true : false,
                    guestPassEnabled: initialData.guestPassEnabled === 1 ? true : false,
                    syncHours: initialData.syncHours === 0 ? undefined : initialData.syncHours
                };

                setIntegrationData(transformedInitialValues);
                setSavedValues(transformedInitialValues);

                // Set the UI toggle states based on the initial values
                setIsImportUsersEnabled(transformedInitialValues.importUsers);
                setIsGuestPassGenerationEnabled(transformedInitialValues.guestPassEnabled);

                const roles = await getRequest(`/orgs/${orgId}/roles`);
                const filteredRoles = roles.data.filter((role: Role) => role.status === 1);
                setRoles(filteredRoles);
            } catch (error) {
                console.log("Failed to fetch data.");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [orgId]);

    // Explicitly set form values after integrationData is loaded
    useEffect(() => {
        if (integrationData && form) {
            form.setFieldsValue(integrationData);
        }
    }, [integrationData, form]);

    const handleGuestPassEnabled = (checked: boolean) => {
        setIsGuestPassGenerationEnabled(checked);
        if (checked) {
            if (form.getFieldValue('guestPassBeforeMins') === undefined) {
                form.setFieldsValue({ guestPassBeforeMins: -1 });
            }
            if (form.getFieldValue('guestPassAfterMins') === undefined) {
                form.setFieldsValue({ guestPassAfterMins: -1 });
            }
        }
    };

    const handleSubmit = useCallback(async ({ values }: IOnSubmitArgs) => {
        setIsSubmitting(true);
        try {
            // Merge with saved values to prevent losing fields from other tabs
            const mergedValues = {
                ...savedValues,
                ...values
            };

            const submittingValues = {
                msiAltaOrgId: mergedValues.msiAltaOrgId,
                msiAltaUserId: mergedValues.msiAltaUserId,
                msiAltaToken: mergedValues.msiAltaToken,
                msiAltaTokenExpUtc: mergedValues.msiAltaTokenExpUtc,
                msiAltaApiHost: mergedValues.msiAltaApiHost,
                msiAltaEntrieIds: mergedValues.msiAltaEntrieIds,
                importUsers: mergedValues.importUsers === true ? 1 : mergedValues.importUsers === false ? 0 : (savedValues?.importUsers === true ? 1 : 0),
                roleId: mergedValues.roleId,
                syncHours: mergedValues.syncHours ? mergedValues.syncHours : 0,
                allowMsiAltaLogin: mergedValues.allowMsiAltaLogin === true ? 1 : mergedValues.allowMsiAltaLogin === false ? 0 : (savedValues?.allowMsiAltaLogin === true ? 1 : 0),
                guestPassEnabled: mergedValues.guestPassEnabled === true ? 1 : mergedValues.guestPassEnabled === false ? 0 : (savedValues?.guestPassEnabled === true ? 1 : 0),
                guestPassBeforeMins: mergedValues.guestPassBeforeMins === undefined ? -1 : mergedValues.guestPassBeforeMins,
                guestPassAfterMins: mergedValues.guestPassAfterMins === undefined ? -1 : mergedValues.guestPassAfterMins
            };
            await patchRequest(`/orgs/${orgId}/integrationMsiAlta`, submittingValues);
            console.log("values", submittingValues);

            // Update savedValues with the merged values (keep as boolean for UI consistency)
            const newSavedValues = {
                ...mergedValues,
                syncHours: mergedValues.syncHours === 0 ? undefined : mergedValues.syncHours
            };
            setSavedValues(newSavedValues);
            // Update form with the new saved values
            form.setFieldsValue(newSavedValues);

            notification.success({
                message: t('notification.success'),
                description: t('integration.notification.updateSuccess'),
                placement: 'bottomRight',
            });
            await dispatch(fetchIntegrationStatus({ orgId }));
        } catch (error) {
            notification.error({
                message: t('notification.success'),
                description: t('integration.notification.submissionError'),
                placement: 'bottomRight',
            });
            console.error("Form submission failed:", error);
        } finally {
            setIsSubmitting(false);
        }
    }, [dispatch, orgId, t, savedValues, form]);

    const handleUninstall = useCallback(async () => {
        confirmModal({
            title: t('integration.confirmUninstall.title'),
            content: t('integration.confirmUninstall.message'),
            okText: t('integration.confirmUninstall.ok'),
            cancelText: t('integration.confirmUninstall.cancel'),
            onOk: async () => {
                setIsSubmitting(true);
                try {
                    await deleteRequest(`/orgs/${orgId}/integrationMsiAlta`);
                    await dispatch(fetchIntegrationStatus({ orgId }));
                    notification.success({
                        message: t('notification.success'),
                        description: t('integration.notification.uninstallSuccess'),
                        placement: 'bottomRight',
                    });
                    setIsConfigureOpen(false); // Go back to the main menu
                    setIsInstalled(false);
                } catch (error) {
                    notification.error({
                        message: t('notification.error'),
                        description: t('integration.notification.submissionError'),
                        placement: 'bottomRight',
                    });
                    console.error("Form submission failed:", error);
                } finally {
                    setIsSubmitting(false);
                }
            },
        });
    }, [dispatch, confirmModal, orgId, t, setIsConfigureOpen, setIsInstalled]);

    const tabItems = [
        {
            key: 'organization',
            label: t('integration.configure.tabs.organization'),
            children: (
                <>
                    <List
                        header={t('integration.configure.organizationTitle')}
                        dataSource={[
                            { name: 'msiAltaOrgId', label: t('integration.configure.selectOrg') }
                        ]}
                        bordered
                        renderItem={item => (
                            <List.Item>
                                <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                    <OpForm.Select
                                        name={item.name}
                                        label={item.label}
                                        placeholder={t('integration.configure.selectOrgPlaceholder')}
                                        options={orgOptions}
                                        disabled={true}
                                    />
                                </div>
                            </List.Item>
                        )}
                    />
                </>
            ),
        },
        {
            key: 'visitor',
            label: t('integration.configure.tabs.visitor'),
            children: (
                <List
                    header={t('integration.configure.visitorTitle')}
                    dataSource={[
                        { label: t('integration.configure.generateGuestPass'), name: 'guestPassEnabled' },
                        ...(isGuestPassGenerationEnabled ? [
                            { label: t('integration.configure.defaultEntries'), name: 'msiAltaEntrieIds' }
                        ] : []),
                    ]}
                    bordered
                    renderItem={item => (
                        <List.Item style={{ width: '100%' }}>
                            {item.name === 'guestPassBeforeMins' ? (
                                <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                    <OpForm.Select
                                        name={item.name}
                                        label={item.label}
                                        placeholder={t('integration.configure.interval.placeholder')}
                                        options={[
                                            { value: -1, label: t('integration.configure.interval.manual') },
                                            { value: 0, label: t('integration.configure.interval.atSignIn') },
                                            { value: 10, label: t('integration.configure.interval.before10') },
                                            { value: 60, label: t('integration.configure.interval.before60') },
                                            { value: 1440, label: t('integration.configure.interval.before1440') },
                                        ]}
                                    />
                                </div>
                            ) : item.name === 'guestPassAfterMins' ? (
                                <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                    <OpForm.Select
                                        name={item.name}
                                        label={item.label}
                                        placeholder={t('integration.configure.interval.placeholder')}
                                        options={[
                                            { value: -1, label: t('integration.configure.interval.manual') },
                                            { value: 10, label: t('integration.configure.interval.after10') },
                                            { value: 60, label: t('integration.configure.interval.after60') },
                                            { value: 1440, label: t('integration.configure.interval.after1440') },
                                        ]}
                                    />
                                </div>
                            ) : item.name === 'msiAltaEntrieIds' ? (
                                <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                    <OpForm.Select
                                        name={item.name}
                                        label={item.label}
                                        placeholder={t('integration.configure.interval.placeholder')}
                                        mode="multiple"
                                        options={entryIdOptions}
                                    />
                                </div>
                            ) : (
                                <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                    <OpForm.Switch
                                        name={item.name}
                                        onChange={handleGuestPassEnabled}
                                    />
                                    <span style={{ marginLeft: 8 }}>{item.label}</span>
                                </div>
                            )}
                        </List.Item>
                    )}
                />
            ),
        },
        {
            key: 'connection',
            label: t('integration.configure.tabs.connection'),
            children: (
                <>
                    <List
                        header={t('integration.configure.authenticationTitle')}
                        dataSource={[
                            { label: t('integration.configure.connected'), name: 'connected', email: email },
                        ]}
                        bordered
                        renderItem={item => (
                            <List.Item style={{ width: '100%' }}>
                                <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                    <span style={{ fontWeight: 'bold' }}>{item.label}:</span>
                                    <span style={{ marginLeft: '8px' }}>{item.email}</span>
                                </div>
                            </List.Item>
                        )}
                    />
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                        <OpButton
                            type="primary"
                            danger
                            onClick={handleUninstall}
                            style={{
                                width: '120px',
                                padding: '4px 0',
                            }}
                        >
                            {t('integration.configure.uninstall')}
                        </OpButton>
                    </div>
                </>

            )
        }
    ];

    // Custom Reset handler
    const handleReset = () => {
        console.log("savedValues", savedValues);
        form.setFieldsValue(savedValues); // Reset the form values

        console.log("savedValues", savedValues);
        setIsImportUsersEnabled(savedValues?.importUsers!);
        setIsGuestPassGenerationEnabled(savedValues?.guestPassEnabled!);
    };

    // Don't render the form until data is loaded
    if (loading || !integrationData) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Spin indicator={<LoadingOutlined spin />} />
            </div>
        );
    }

    return (
        <OpForm
            form={form}
            onSubmit={handleSubmit}
            initialValues={integrationData}
            hasError={false}
            defaultButtons={false}
        >
            {isSubmitting ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Spin indicator={<LoadingOutlined spin />} />
                </div>
            ) : (
                <>
                    <OpTabs activeKey={activeKey} onChange={setActiveKey} items={tabItems} />
                    {(activeKey === 'organization' || activeKey === 'visitor') ? (
                        <div style={{ 'marginTop': '16px' }}>
                            <CustomFormButtons
                                form={form}
                                isFormLoading={loading}
                                isSubmitButtonLoading={isSubmitting}
                                onResetClick={handleReset}
                            />
                        </div>
                    ) : undefined}
                </>
            )}
        </OpForm>
    );
};

export default IntegrationConfigure;